import React, { useState, useEffect } from "react";
import { isBrowser } from "../utils/ssrUtils";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackgroundScene from "../components/BackgroundScene";
import SEO from "../components/SEO";
import { usePlausible } from "../hooks/usePlausible";
import { createEventProps, CATEGORIES } from "../utils/analytics";
import styles from "../routes/Home/Home.module.css";
import { withAssetPath, buildCanonicalUrl } from "../utils/basePath";

const DownloadMobile: React.FC = () => {
  const trackEvent = usePlausible();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!isBrowser()) return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleAppStoreClick = () => {
    trackEvent(
      "Download",
      createEventProps(CATEGORIES.DOWNLOAD, "ios-app-store", "mobile")
    );
  };

  const handlePlayStoreClick = () => {
    trackEvent(
      "Download",
      createEventProps(CATEGORIES.DOWNLOAD, "android-play-store", "mobile")
    );
  };

  return (
    <div
      className={styles.home}
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <SEO
        title="Download Rythm Mobile App - iOS & Android"
        description="Download the Rythm mobile app for iOS and Android. Take your music experience on the go with the Rythm app."
        canonicalUrl={buildCanonicalUrl("/download/mobile", { forceRoot: true })}
        keywords="rythm mobile app, ios app, android app, app store, play store, music app download"
        ogImage="https://rythm.fm/assets/images/download-og.png"
        ogImageAlt="Download Rythm Mobile App - Available on iOS and Android"
        schemaType="SoftwareApplication"
      />
      <BackgroundScene modelScale={2.0} />
      <Header />
      <main
        className={styles.mainContent}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "flex-start" : "center",
          position: "relative",
        }}
      >
        {/* Cassette video background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: "absolute",
            top: "90%",
            left: "10%",
            transform: "translate(-50%, -50%)",
            width: "600px",
            height: "auto",
            zIndex: 2,
            opacity: 0.8,
            rotate: "10deg",
            pointerEvents: "none",
            objectFit: "contain",
          }}
        >
          <source
            src={withAssetPath("/assets/video/cassette.mp4")}
            type="video/mp4"
          />
        </video>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            textAlign: "center",
            padding: isMobile ? "1rem" : "2rem",
            paddingTop: isMobile ? "1rem" : "2rem",
            position: "relative",
            zIndex: 10,
            width: "100%",
            maxWidth: "800px",
          }}
        >
          <h1
            style={{
              fontSize: "clamp(20px, 5vw, 26px)",
              fontWeight: 700,
              background:
                "linear-gradient(135deg, #7C3AED 0%,rgb(72, 80, 236) 50%,rgb(11, 73, 245) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              marginBottom: "0.75rem",
              lineHeight: 1.2,
            }}
          >
            Download Mobile App
          </h1>

          {/* Rythm Logo Video */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "1rem",
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              style={{
                width: isMobile ? "clamp(150px, 45vw, 200px)" : "280px",
                height: "auto",
                objectFit: "contain",
                transition: "transform 0.3s ease",
                opacity: 1,
              }}
            >
              <source
                src={withAssetPath("/assets/video/little-logo_5.webm")}
                type="video/webm"
              />
              <source
                src={withAssetPath("/assets/video/little-logo.mp4")}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>

          <p
            style={{
              fontSize: "clamp(14px, 4vw, 18px)",
              color: "rgba(255, 255, 255, 0.8)",
              marginBottom: "1.5rem",
              maxWidth: "650px",
              lineHeight: 1.6,
              padding: "0 0.5rem",
            }}
          >
            Take your music experience on the go with the Rythm mobile app
          </p>

          {/* App Store Badges */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1.5rem",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "2rem",
              padding: "1.5rem",
              background: "rgba(255, 255, 255, 0.03)",
              borderRadius: "16px",
              border: "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            {/* App Store Badge */}
            <a
              href="https://apps.apple.com/app/rythm-music/id6443958451"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleAppStoreClick}
              style={{
                transition: "transform 0.2s ease, opacity 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0.5rem",
                borderRadius: "12px",
                minWidth: "200px",
                minHeight: "60px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.opacity = "0.9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.opacity = "1";
              }}
            >
              <img
                src={withAssetPath("/assets/images/app-store-badge.svg")}
                alt="Download on the App Store"
                style={{
                  height: "clamp(56px, 10vw, 72px)",
                  width: "auto",
                  maxWidth: "100%",
                }}
              />
            </a>

            {/* Play Store Badge */}
            <a
              href="https://play.google.com/store/apps/details?id=com.rythm"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handlePlayStoreClick}
              style={{
                transition: "transform 0.2s ease, opacity 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0.5rem",
                borderRadius: "12px",
                minWidth: "200px",
                minHeight: "60px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.opacity = "0.9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.opacity = "1";
              }}
            >
              <img
                src={withAssetPath("/assets/images/google-play-badge.svg")}
                alt="Get it on Google Play"
                style={{
                  height: "clamp(56px, 10vw, 72px)",
                  width: "auto",
                  maxWidth: "100%",
                }}
              />
            </a>
          </div>

          <div
            style={{
              marginTop: "1rem",
              padding: "1.5rem",
              background: "rgba(255, 255, 255, 0.04)",
              borderRadius: "12px",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.07)",
            }}
          >
            <p
              style={{
                color: "rgba(255, 255, 255, 0.5)",
                marginBottom: "1rem",
              }}
            >
              Looking for the desktop app?
            </p>
            <a
              href="/download"
              style={{
                padding: "0.75rem 1.5rem",
                background: "rgba(255, 255, 255, 0.04)",
                color: "rgba(255, 255, 255, 0.8)",
                textDecoration: "none",
                borderRadius: "8px",
                fontSize: "0.9rem",
                fontWeight: 500,
                transition: "background 0.3s ease",
                display: "inline-block",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
              }}
            >
              Download for Desktop
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DownloadMobile;
